import React, { useRef, useEffect } from 'react';
import { components } from 'react-select';
import AsyncSelect from 'react-select/async-creatable';
import styled from 'styled-components';
import Fuse from 'fuse.js';
import { FieldContainer, Field, Label, FieldBefore } from '../field';
import { Icon } from '../icon';
import { useFieldValidation } from '../../hooks/validation-field';
import { makeCustomSup, makeAutocomplete } from './mixins';
import { Helper } from '../Helper';
const SelectField = styled(AsyncSelect) `
  ${(props) => makeAutocomplete(props)}
`;
const CustomSup = styled.sup `
  ${() => makeCustomSup()}
`;
const ClearIndicator = (props) => {
    return (React.createElement(components.ClearIndicator, Object.assign({}, props),
        React.createElement(Icon, { name: "icon-light-close", color: "rgba(0, 0, 24, 0.16)", size: "16" })));
};
export const Autocomplete = ({ id, name, value, options, defaultOptions, asyncOptions, placeholder = 'Seleccione una opción', onChange, onInputChange, onBlur, onFocus, disabled = false, cacheOptions = true, minQueryLength = 2, loadingMessage = 'Cargando ...', maxMenuHeight = 300, threshold = 0.7, ignoreLocation = true, rules, errors, fieldOptions, formatOptionLabel, isClearable = false, isCreatable = false, isEditable = false, blurOnSelect = false, createLabel = '', noBottomMargin, noOptionsMsg, }) => {
    const defaultNoOpts = minQueryLength > 1 ? `Ingresa al menos ${minQueryLength} caracteres` : 'Ingresa al menos un caracter';
    const { fieldStates, classes, inputValue, setInputValue, setValue, setActive } = useFieldValidation(rules, errors, name, disabled);
    const extraProps = isEditable ? { inputValue } : null;
    const fuse = useRef();
    useEffect(() => {
        if (options) {
            fuse.current = new Fuse(options, {
                keys: ['label'],
                threshold,
                ignoreLocation,
            });
        }
    }, [ignoreLocation, options, threshold]);
    useEffect(() => {
        if (isEditable && inputValue !== (value === null || value === void 0 ? void 0 : value.value)) {
            setInputValue(value);
        }
    }, [value]);
    const handleOnChange = (option, callback) => {
        setValue(option);
        if (isEditable) {
            setInputValue(option);
        }
        if (callback) {
            callback(name, option);
        }
    };
    const handleOnBlur = (event, callback) => {
        setActive(false);
        if (callback) {
            callback(event);
        }
    };
    const loadOptionsHandler = (query) => {
        var _a, _b;
        if (asyncOptions && query.length >= minQueryLength) {
            return asyncOptions(query);
        }
        if (options && query.length >= minQueryLength) {
            const filtered = (_b = (_a = fuse.current) === null || _a === void 0 ? void 0 : _a.search(query, { limit: 100 }).map((v) => v.item)) !== null && _b !== void 0 ? _b : [];
            return Promise.resolve(filtered);
        }
        return Promise.resolve();
    };
    return (React.createElement(FieldContainer, null,
        fieldOptions && fieldOptions.label && (React.createElement(Label, { htmlFor: id || `autocomplete-${name}` },
            fieldOptions.label,
            rules && rules.required && React.createElement(CustomSup, null, "*"))),
        React.createElement(Field, Object.assign({}, fieldOptions, { states: Object.assign({}, fieldStates), className: classes }),
            fieldOptions && fieldOptions.before && React.createElement(FieldBefore, null, fieldOptions.before),
            React.createElement(SelectField, Object.assign({ id: id || `autocomplete-${name}`, name: name, placeholder: placeholder, defaultOptions: defaultOptions, cacheOptions: cacheOptions, loadOptions: loadOptionsHandler, createOptionPosition: "first", formatCreateLabel: (labelInputValue) => `${createLabel}${labelInputValue}`, formatOptionLabel: formatOptionLabel, noOptionsMessage: () => React.createElement(React.Fragment, null, noOptionsMsg ? noOptionsMsg : defaultNoOpts), loadingMessage: () => React.createElement(React.Fragment, null, loadingMessage), isDisabled: disabled, onChange: (option) => handleOnChange(option, onChange), onInputChange: (option, event) => isEditable && event.action === 'input-change' && handleOnChange(option, onInputChange), onBlur: (event) => handleOnBlur(event, onBlur), onFocus: onFocus, onMenuOpen: () => setActive(true), value: value !== null && value !== void 0 ? value : null, components: { ClearIndicator }, classNamePrefix: "select", blurInputOnSelect: blurOnSelect, isClearable: isClearable, isValidNewOption: (opt) => (isCreatable ? opt.length >= minQueryLength : false), maxMenuHeight: maxMenuHeight }, fieldOptions, extraProps))),
        React.createElement(Helper, { name: name, fieldOptions: fieldOptions, errors: errors, noBottomMargin: noBottomMargin })));
};
